import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { toggleVariants } from '@/components/buttons/Toggle/Toggle';
import { cn } from '@/lib/utils';

import { useToggleGroupContext } from './ToggleGroupContext';

export type ToggleGroupItemProps = TogglePrimitive.Props & VariantProps<typeof toggleVariants>;

const toggleGroupItemVariants = cva(['shrink-0 px-2 focus:z-10 focus-visible:z-10'], {
  variants: {
    variant: {
      secondary: [
        'border-gray-900 border',
        'group-data-[spacing=0]/toggle-group:rounded-none',

        'group-data-horizontal/toggle-group:data-[spacing=0]:border-l-0 group-data-horizontal/toggle-group:data-[spacing=0]:first:border-l',
        'group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-xs group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-xs',

        'group-data-vertical/toggle-group:data-[spacing=0]:border-t-0 group-data-vertical/toggle-group:data-[spacing=0]:first:border-t',
        'group-data-vertical/toggle-group:data-[spacing=0]:first:rounded-t-xs group-data-vertical/toggle-group:data-[spacing=0]:last:rounded-b-xs',
      ],
      ghost: [
        'group-data-[spacing=0]/toggle-group:rounded-none',

        'group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-xs group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-xs',
        'group-data-vertical/toggle-group:data-[spacing=0]:first:rounded-t-xs group-data-vertical/toggle-group:data-[spacing=0]:last:rounded-b-xs',
      ],
    },
  },
});

const ToggleGroupItem = ({
  className,
  children,
  variant,
  size,
  shape = 'square',
  ...props
}: ToggleGroupItemProps) => {
  const context = useToggleGroupContext();
  const resolvedVariant = variant ?? context.variant;
  const resolvedSize = size ?? context.size;
  const isJoined = context.spacing === 0;

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-spacing={context.spacing}
      className={cn(
        toggleGroupItemVariants({
          variant: resolvedVariant,
        }),
        toggleVariants({
          variant: resolvedVariant,
          size: resolvedSize,
          shape: isJoined ? 'square' : shape,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
};

export default ToggleGroupItem;
